// ============================================================
//  IMAGES  –  yahan sirf apni image ke paths badalne hain
//  Files public/GPMS_img/ me rakho (path me "public" nahi likhna)
// ============================================================
export const IMAGES = {
  intro: "/ImagesNew/SOLUTION_IMAGE.png",

  cap1: "/GPMS_img/coreAnalyticsImg.jpg",
  cap2: "/GPMS_img/GPMS_VISION.png",
  cap3: "/GPMS_img/GpmsGateforcasting.jpg",
  cap4: "/GPMS_img/GpmsDwellRiskIntelligence.jpg",

  core: "/GPMS_img/coreAnalyticsImg.jpg",
  vision: "/GPMS_img/GPMS_VISION.png",
  gate: "/GPMS_img/GpmsGateforcasting.jpg",
  dwell: "/GPMS_img/GpmsDwellRiskIntelligence.jpg",
};

// ---------------- Intro ("The Solution") ----------------
export const INTRO = {
  label: "THE SOLUTION",
  title: "Introducing GPMS",
  highlight: "One Platform. Multiple Systems. Unified Intelligence.",
  paragraphs: [
    "Global Port Management System bridges the gap between disconnected operational systems and effective decision-making.",
    "Built on the powerful Qlik analytics platform, GPMS integrates existing data sources to transform complex operational information into meaningful dashboards, performance indicators, and predictive insights.",
    "From monitoring ongoing terminal activities to forecasting future congestion and identifying containers at risk of excessive dwell, GPMS helps port operators move from reactive reporting toward proactive operational management.",
  ],
  chips: [
    "Monitor operations in real-time",
    "Predict risks before they occur",
    "Improve decision-making speed",
    "Optimize resource utilization",
  ],
};

// ---------------- Four Core Capabilities ----------------
export const CAPABILITIES_HEAD = {
  badge: "CORE CAPABILITIES",
  title: "FOUR CORE CAPABILITIES",
  subtitle:
    "Unified analytics, AI vision, and predictive intelligence to move port operations from reactive reporting to proactive management.",
};

export const CAPABILITIES = [
  {
    id: "core-analytics",
    number: "01",
    tag: "CORE ANALYTICS",
    title: "GPMS Core Analytics",
    desc: "Unified live and historical analytics for port-wide operational visibility.",
    image: IMAGES.cap1,
  },
  {
    id: "gpms-vision",
    number: "02",
    tag: "AI VISION",
    title: "GPMS Vision",
    desc: "AI-powered vehicle and container identification using computer vision technology.",
    image: IMAGES.cap2,
  },
  {
    id: "gate-forecasting",
    number: "03",
    tag: "GATE FORECAST",
    title: "Predictive Gate Congestion & Truck TAT Forecasting",
    desc: "Anticipate traffic buildup and truck turnaround delays before they affect operations.",
    image: IMAGES.cap3,
  },
  {
    id: "dwell-risk",
    number: "04",
    tag: "DWELL RISK",
    title: "Container Dwell Risk Intelligence",
    desc: "Identify containers at risk of extended dwell and improve yard capacity planning.",
    image: IMAGES.cap4,
  },
];

// ---------------- Detail sections ----------------
export const DETAILS = [
  {
    id: "core-analytics",
    eyebrow: "GPMS Core Analytics",
    title: "Transform Disconnected Port Data into Meaningful Operational Intelligence",
    image: IMAGES.core,
    imageTag: "CORE ANALYTICS",
    intro: [
      "GPMS Core Analytics provides a comprehensive view of port activities by bringing information from multiple operational databases into a centralized analytics environment.",
      "Powered by Qlik, the platform converts complex datasets into interactive dashboards designed for operational monitoring, performance evaluation, and management decision-making.",
    ],
    blocks: [
      {
        title: "Seamless Multi-System Data Integration",
        text: ["GPMS is designed to integrate with existing port applications and databases, including:"],
        list: [
          "Navis and Terminal Operating Systems (TOS)",
          "Container Management Systems (CMS)",
          "Gate Management Systems",
          "Yard and Container Movement Data",
          "Customs and Clearance Data",
          "ERP and Enterprise Databases",
          "Other Port Operational Systems",
        ],
        note: "Integration depends on the source systems and authorized data access available at each terminal.",
      },
      {
        title: "Live Operational Analytics",
        subtitle: "Know What's Happening Across Your Port.",
        text: [
          "Access continuously updated operational information to monitor terminal activities and identify emerging challenges.",
        ],
        listLabel: "Key dashboard areas include:",
        list: [
          "Gate traffic and truck turnaround performance",
          "Container movements and throughput",
          "Yard occupancy and utilization",
          "Vessel and berth operational performance",
          "Equipment productivity and utilization",
          "Shift-wise operational performance",
        ],
        note: "Live dashboard refresh frequency depends on the available data integration and deployment configuration.",
      },
      {
        title: "Historical & Performance Analytics",
        subtitle: "Understand Past Performance. Improve Future Operations.",
        text: [
          "Analyze operational trends and historical data to identify inefficiencies, compare performance, and strengthen planning.",
        ],
        listLabel: "Key analytical capabilities include:",
        list: [
          "Daily, weekly, and monthly performance trends",
          "Container throughput analysis",
          "Truck turnaround time analysis",
          "Container dwell time analysis",
          "Yard utilization and congestion trends",
          "Vessel turnaround performance",
          "Operational KPI comparison",
        ],
      },
      {
        title: "Management Decision Dashboards",
        subtitle: "Better Visibility for Better Decisions.",
        text: [
          "GPMS provides consolidated performance views that help terminal managers and leadership teams evaluate operational efficiency, identify areas requiring attention, and make informed decisions based on reliable data.",
        ],
        highlight: {
          label: "Business Value",
          text: "Centralized visibility, reduced manual reporting, consistent performance measurement, and more informed operational planning.",
        },
      },
    ],
  },

  {
    id: "gpms-vision",
    eyebrow: "GPMS Vision",
    kicker: "AI-Powered Vehicle & Container Identification",
    title: "Turn Visual Information into Reliable Operational Data",
    image: IMAGES.vision,
    imageTag: "OCR + AI",
    intro: [
      "GPMS Vision extends port analytics with computer vision technology, supporting automated vehicle and container identification at terminal gates and other configured monitoring points.",
      "Using camera imagery and AI-based recognition, GPMS Vision helps reduce manual identification efforts and improves the consistency of information used in gate-related operations.",
    ],
    blocks: [
      {
        title: "Intelligent Vehicle Identification",
        text: [
          "Automatically identify vehicle registration numbers through AI-powered Automatic Number Plate Recognition (ANPR).",
          "This supports vehicle identification, transaction matching, and improved visibility into truck movements.",
        ],
      },
      {
        title: "Container Number Recognition",
        text: [
          "Capture and interpret visible container identification numbers using Optical Character Recognition (OCR).",
          "Recognized information can be compared with available operational records to identify discrepancies and support accurate container identification.",
        ],
      },
      {
        title: "Visual Verification",
        text: [
          "Use captured images and recognition results to support operational verification and investigate vehicle or container identification discrepancies.",
        ],
      },
      {
        title: "Confidence-Based Identification",
        text: [
          "AI recognition confidence helps distinguish reliable identification results from uncertain readings that may require operator verification.",
        ],
      },
    ],
    benefitsTitle: "How GPMS Vision Benefits Port Operations",
    benefits: [
      "Reduces dependence on manual vehicle and container identification",
      "Helps minimize data entry errors",
      "Improves consistency of gate-related information",
      "Supports faster identification and verification",
      "Strengthens operational traceability",
    ],
    closing: "GPMS Vision connects physical port activity with digital operational information.",
  },

  {
    id: "gate-forecasting",
    eyebrow: "Predictive Gate Congestion & Truck TAT Forecasting",
    kicker: "From Monitoring Traffic to Anticipating Delays",
    title: "Predict Gate Congestion Before It Impacts Operations",
    image: IMAGES.gate,
    imageTag: "GATE FORECAST",
    intro: [
      "Gate congestion is one of the major operational challenges affecting container terminals.",
      "Heavy truck arrivals, peak operating periods, and changing terminal conditions can lead to increased waiting times, reduced gate efficiency, and disruptions to logistics schedules.",
      "GPMS uses predictive analytics to anticipate congestion risk and estimate Truck Turnaround Time (TAT), enabling terminal operators to plan more effectively.",
    ],
    blocks: [
      {
        title: "Intelligent Gate Congestion Forecasting",
        text: [
          "Analyze historical gate transactions and available operational signals to estimate potential traffic buildup during upcoming periods.",
          "Forecasts can support short-term planning for the next 30 minutes, hour, or other configured operating windows.",
        ],
      },
      {
        title: "Truck Turnaround Time Prediction",
        text: [
          "Estimate expected truck processing and turnaround durations using historical patterns and available live operational information.",
          "These insights help operators understand potential delays and evaluate gate performance.",
        ],
      },
      {
        title: "Peak Traffic Pattern Analysis",
        text: [
          "Identify recurring congestion trends based on operating hours, shifts, traffic volumes, and historical demand patterns.",
        ],
      },
      {
        title: "Gate Performance Intelligence",
        text: [
          "Compare actual turnaround performance against predicted operating conditions to better understand congestion patterns and operational inefficiencies.",
        ],
      },
    ],
    benefitsTitle: "How Predictive Gate Intelligence Helps",
    benefits: [
      "Provides earlier visibility into potential congestion",
      "Supports improved gate and manpower planning",
      "Helps operators anticipate extended truck turnaround times",
      "Improves coordination during peak traffic periods",
      "Supports more efficient movement of trucks through terminal operations",
    ],
    closing: "Don't just monitor congestion. Anticipate it.",
  },

  {
    id: "dwell-risk",
    eyebrow: "Container Dwell Risk Intelligence",
    kicker: "Smarter Dwell Management. Better Yard Utilization.",
    title: "Identify Container Delays Before They Become Operational Bottlenecks",
    image: IMAGES.dwell,
    imageTag: "DWELL RISK",
    intro: [
      "Extended container dwell time is a significant challenge for ports and container terminals.",
      "Containers that remain in the yard longer than expected consume valuable storage capacity, contribute to congestion, and may affect overall terminal productivity.",
      "GPMS Container Dwell Risk Intelligence applies predictive analytics to historical and available current container data to identify containers at risk of exceeding expected dwell periods.",
    ],
    blocks: [
      {
        title: "AI-Based Dwell Risk Prediction",
        text: [
          "Evaluate relevant container characteristics and operational conditions to estimate the likelihood of extended dwell.",
        ],
      },
      {
        title: "Container Risk Classification",
        text: [
          "Categorize containers according to predicted dwell risk, helping operations teams identify which containers may require closer attention.",
        ],
      },
      {
        title: "Dwell Aging Analysis",
        text: [
          "Analyze how long containers remain in terminal facilities through configurable aging categories and historical comparisons.",
        ],
      },
      {
        title: "Risk Driver Analysis",
        text: [
          "Examine relevant factors such as container status, historical dwell behavior, clearance delays, and available operational conditions that may contribute to extended dwell.",
        ],
      },
      {
        title: "Yard Capacity Insights",
        text: [
          "Understand how container dwell patterns influence yard occupancy and support more effective operational planning.",
        ],
      },
    ],
    benefitsTitle: "How Container Dwell Intelligence Helps",
    benefits: [
      "Provides early visibility into potential long-dwell containers",
      "Helps teams prioritize containers requiring attention",
      "Supports improved yard utilization",
      "Assists in reducing dwell-related operational pressure",
      "Strengthens container movement and capacity planning",
    ],
    closing: "Identify dwell risks early. Protect valuable terminal capacity.",
  },
];